/**
 * List of free email provider domains (based on HubSpot's blocked domains list)
 * These are considered "dangerous" for business use as they:
 * - Are often used for spam/fake accounts
 * - Have higher bounce rates
 * - Are not tied to a business entity
 *
 * Source: https://knowledge.hubspot.com/forms/what-domains-are-blocked-when-using-the-forms-email-domains-to-block-feature
 */

export interface EmailDomainCategory {
  name: string;
  domains: string[];
}

export const FREE_EMAIL_DOMAIN_CATEGORIES: EmailDomainCategory[] = [
  {
    name: 'Major Providers',
    domains: [
      'aol.com',
      'gmail.com',
      'hotmail.com',
      'icloud.com',
      'live.com',
      'mail.com',
      'outlook.com',
      'protonmail.com',
      'yahoo.com',
      'yandex.com',
      'zoho.com',
    ],
  },
  {
    name: 'Google Variants',
    domains: ['googlemail.com'],
  },
  {
    name: 'Microsoft Variants',
    domains: [
      'hotmail.co.uk',
      'hotmail.de',
      'hotmail.es',
      'hotmail.fr',
      'hotmail.it',
      'live.co.uk',
      'live.de',
      'live.fr',
      'live.it',
      'live.nl',
      'msn.com',
      'outlook.de',
      'outlook.es',
      'outlook.fr',
      'outlook.it',
    ],
  },
  {
    name: 'Yahoo Variants',
    domains: ['rocketmail.com', 'yahoo.co.in', 'yahoo.co.uk', 'yahoo.com.br', 'yahoo.de', 'yahoo.es', 'yahoo.fr', 'yahoo.it', 'ymail.com'],
  },
  {
    name: 'GMX Variants',
    domains: ['gmx.at', 'gmx.ch', 'gmx.com', 'gmx.de', 'gmx.fr', 'gmx.net'],
  },
  {
    name: 'Asian Providers',
    domains: ['126.com', '163.com', 'daum.net', 'hanmail.net', 'naver.com', 'qq.com', 'sina.com', 'sohu.com'],
  },
  {
    name: 'European Providers',
    domains: [
      'alice.it',
      'free.fr',
      'freenet.de',
      'libero.it',
      'mail.ru',
      'orange.fr',
      'rambler.ru',
      't-online.de',
      'tin.it',
      'tiscali.co.uk',
      'tiscali.it',
      'wanadoo.fr',
      'web.de',
    ],
  },
  {
    name: 'UK Providers',
    domains: ['blueyonder.co.uk', 'btinternet.com', 'ntlworld.com', 'o2.co.uk', 'sky.com', 'talktalk.net', 'virginmedia.com'],
  },
  {
    name: 'US ISP Providers',
    domains: [
      'att.net',
      'bellsouth.net',
      'charter.net',
      'comcast.net',
      'cox.net',
      'earthlink.net',
      'juno.com',
      'netzero.net',
      'sbcglobal.net',
      'verizon.net',
    ],
  },
  {
    name: 'Other Providers',
    domains: ['fastmail.com', 'inbox.com', 'rediffmail.com'],
  },
  {
    name: 'Temporary/Disposable',
    domains: [
      '10minutemail.com',
      'getnada.com',
      'guerrillamail.com',
      'maildrop.cc',
      'mailinator.com',
      'temp-mail.org',
      'tempmail.com',
      'throwaway.email',
      'trashmail.com',
    ],
  },
];

// Flat array for backward compatibility
export const FREE_EMAIL_DOMAINS = FREE_EMAIL_DOMAIN_CATEGORIES.flatMap((cat) => cat.domains);

/**
 * Check if an email address uses a free email domain
 */
export function isFreeEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const domain = email.toLowerCase().split('@')[1];
  return domain ? FREE_EMAIL_DOMAINS.includes(domain) : false;
}

/**
 * Extract email domain from an email address
 */
export function getEmailDomain(email: string): string | null {
  if (!email || typeof email !== 'string') {
    return null;
  }

  const parts = email.split('@');
  return parts.length === 2 ? parts[1].toLowerCase() : null;
}
